import React, { useMemo } from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import type { ChartDataItem } from 'utils/chart-options';

import BarChart from '../BarChart';
import MultilineChart from '../MultilineChart';
import SidewaysBarChart from '../SidewaysBarChart';
import type { ChartPreviewRequest } from './chartPreviewUtils';
import PieChartPreview from './PieChartPreview';

type Props = {
  chart: ChartPreviewRequest;
  height?: number | string;
  width?: number | string;
};

const DEFAULT_HEIGHT = 240;

const AIChartPreview = ({ chart, height, width }: Props): React.JSX.Element | null => {
  const theme = useTheme();
  const data = useMemo<ChartDataItem[]>(
    () =>
      chart.data.map((row) => ({
        ...row,
        category: String(row[chart.categoryField] ?? ''),
      })),
    [chart.categoryField, chart.data],
  );
  const colors = theme.palette.data.sequence;
  const numericHeight = typeof height === 'number' ? height : DEFAULT_HEIGHT;
  const containerSx = { width: width ?? '100%', height: height ?? DEFAULT_HEIGHT };

  switch (chart.chartType) {
    case 'column':
      return (
        <Box sx={containerSx}>
          <BarChart
            data={data}
            orderedFields={chart.valueFields}
            height={height}
            animated={false}
            colorOverrides={colors}
            showGridLines={false}
            tooltipTotalLabel=''
          />
        </Box>
      );
    case 'bar':
    case 'sidewaysBar': {
      const valueField = chart.valueFields[0];
      const sidewaysData = data.flatMap((row) => {
        const total = row[valueField];
        return typeof total === 'number'
          ? [
              {
                category: String(row.category),
                total,
              },
            ]
          : [];
      });

      return (
        <Box sx={{ width: width ?? '100%' }}>
          <SidewaysBarChart
            data={sidewaysData}
            orderedFields={sidewaysData.map(({ category }) => category)}
            variant='simple'
            animated={false}
            renderer='svg'
          />
        </Box>
      );
    }
    case 'line':
    case 'area':
      return (
        <Box sx={containerSx}>
          <MultilineChart
            data={data}
            orderedFields={chart.valueFields}
            height={numericHeight}
            colorOverrides={colors}
            areaOpacity={chart.chartType === 'area' ? 0.2 : 0}
            curvy={false}
            hideAxes={false}
            nearestSeriesTooltip
          />
        </Box>
      );
    case 'pie':
      return <PieChartPreview chart={chart} height={height} width={width} />;
    default:
      return null;
  }
};

export default AIChartPreview;
